import React, { useEffect, useMemo, useRef, useState } from "react";
import LeftAngle from "../../assets/image/left-angle.svg";
import RightAngle from "../../assets/image/right-angle.svg";
import DownArrow from "../../assets/image/down-arrow.svg";
import {
  compareMonthYear,
  getSalaryAwardsMonthBounds,
} from "./monthYearBounds";

const MONTHS_SHORT = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const C = {
  text: "#000000",
  muted: "#757575",
  border: "#E0E0E0",
  selected: "#000053",
};

/**
 * Combined month + year picker (grid popover). Bounds default to Salary Awards rules:
 * from April 2026 through the current calendar month.
 */
const MonthYearPicker = ({
  month,
  year,
  onChange,
  label = "Month",
  id = "month-year-picker",
  className = "",
  min: minProp,
  max: maxProp,
}) => {
  const ref = useRef(null);
  const [open, setOpen] = useState(false);
  const bounds = useMemo(() => {
    const defaults = getSalaryAwardsMonthBounds();
    return {
      min: minProp ?? defaults.min,
      max: maxProp ?? defaults.max,
    };
  }, [minProp, maxProp]);

  const selectedYear = Number(year);
  const selectedMonth = Number(month);

  const [viewYear, setViewYear] = useState(selectedYear);

  useEffect(() => {
    if (open) setViewYear(selectedYear);
  }, [open, selectedYear]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const displayLabel = useMemo(() => {
    if (!selectedYear || !selectedMonth) return "";
    const d = new Date(selectedYear, selectedMonth - 1, 1);
    return d.toLocaleDateString("en-IN", { month: "long", year: "numeric" });
  }, [selectedYear, selectedMonth]);

  const canPrevYear = viewYear > bounds.min.year;
  const canNextYear = viewYear < bounds.max.year;

  const isMonthEnabled = (monthIndex) => {
    const candidate = { year: viewYear, month: monthIndex + 1 };
    return (
      compareMonthYear(candidate, bounds.min) >= 0 &&
      compareMonthYear(candidate, bounds.max) <= 0
    );
  };

  const handleSelectMonth = (monthIndex) => {
    if (!isMonthEnabled(monthIndex)) return;
    const nextMonth = String(monthIndex + 1).padStart(2, "0");
    onChange({ month: nextMonth, year: String(viewYear) });
    setOpen(false);
  };

  return (
    <div className={`relative ${className}`} ref={ref}>
      <label
        className="block text-xs font-UbuntuMedium mb-1.5"
        style={{ color: C.muted }}
        htmlFor={id}
      >
        {label}
      </label>
      <button
        id={id}
        type="button"
        className="form-control h-11 rounded-lg border text-sm w-full min-w-[180px] flex items-center justify-between gap-2 px-3 bg-white text-left"
        style={{ borderColor: C.border, color: C.text }}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="dialog"
      >
        <span className="truncate">{displayLabel || "Select month"}</span>
        <img src={DownArrow} alt="" className="w-3 h-3 shrink-0 opacity-60" />
      </button>

      {open && (
        <div
          className="absolute left-0 top-[calc(100%+4px)] z-30 rounded-lg border bg-white shadow-lg p-4 min-w-[240px]"
          style={{ borderColor: C.border }}
          role="dialog"
          aria-label="Choose month and year"
        >
          <div className="flex items-center justify-between mb-4">
            <button
              type="button"
              className="h-8 w-8 flex items-center justify-center rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
              onClick={() => canPrevYear && setViewYear((y) => y - 1)}
              disabled={!canPrevYear}
              aria-label="Previous year"
            >
              <img src={LeftAngle} alt="" className="w-3 h-3" />
            </button>
            <span className="text-sm font-UbuntuMedium" style={{ color: C.text }}>
              {viewYear}
            </span>
            <button
              type="button"
              className="h-8 w-8 flex items-center justify-center rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
              onClick={() => canNextYear && setViewYear((y) => y + 1)}
              disabled={!canNextYear}
              aria-label="Next year"
            >
              <img src={RightAngle} alt="" className="w-3 h-3" />
            </button>
          </div>

          <div className="grid grid-cols-3 gap-2">
            {MONTHS_SHORT.map((shortLabel, index) => {
              const enabled = isMonthEnabled(index);
              const isSelected =
                enabled &&
                viewYear === selectedYear &&
                index + 1 === selectedMonth;
              return (
                <button
                  key={shortLabel}
                  type="button"
                  disabled={!enabled}
                  onClick={() => handleSelectMonth(index)}
                  className={`h-9 rounded-full text-sm font-UbuntuMedium transition-colors ${
                    isSelected
                      ? "text-white"
                      : enabled
                        ? "text-black hover:bg-gray-100"
                        : "text-gray-300 cursor-not-allowed"
                  }`}
                  style={
                    isSelected ? { backgroundColor: C.selected } : undefined
                  }
                >
                  {shortLabel}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default MonthYearPicker;
